import {
  filterHasSecondaryPosition,
  filterPosition,
  getUpdateLineupsPayload,
  getUpdateLineupsPlayerPayload,
  isPlayerSame,
  LineupModel,
  withHasSecondaryPosition,
} from '@collector/desktop-feature-lineups'
import { Events } from '@collector/sportsapi-client'
import {
  SubParticipantFlag,
  SubParticipantPosition,
} from '@collector/sportsapi-client-legacy'
import { SubParticipant } from '@collector/sportsapi-types'

export type PositionKey = 'batter' | 'pitcher' | 'coach' | 'willNotPlay'
export type BaseballLineupModel = LineupModel<PositionKey>

export function mapPlayersWithSecondaryPosition(
  players: SubParticipant[],
): ReturnType<typeof withHasSecondaryPosition>[] {
  return players.map((player) => withHasSecondaryPosition(player))
}

export function createBaseballLineupModel(
  players: SubParticipant[],
): BaseballLineupModel {
  const mapped = mapPlayersWithSecondaryPosition(players)

  return {
    batter: filterPosition(mapped, SubParticipantPosition.Batter).concat(
      filterHasSecondaryPosition(
        filterPosition(mapped, SubParticipantPosition.Pitcher),
      ),
    ),
    pitcher: filterPosition(mapped, SubParticipantPosition.Pitcher),
    coach: filterPosition(mapped, SubParticipantPosition.Coach),
    willNotPlay: filterPosition(mapped, SubParticipantPosition.WillNotPlay),
  }
}

/**
 * Builds the `POST /sub-participants-save` squad.
 *
 * Pitchers who also appear in the batting rotation are sent as `position: pitcher`
 * with `flags: ['possible_batter']`. Bench pitchers send `flags: []` so a previously
 * stored possible-batter flag is cleared. Array order of the batting rotation is
 * the batting order.
 */
export function getBaseballSquadPayload(
  lineup: BaseballLineupModel,
): Events.SubParticipantsSave.SubParticipantsSavePostSquad[] {
  const battingRotationPayload = lineup.batter.map((batter) => {
    const pitcher = lineup.pitcher.find((player) =>
      isPlayerSame(player, batter),
    )
    const payload = getUpdateLineupsPlayerPayload(
      pitcher ?? batter,
      pitcher ? SubParticipantPosition.Pitcher : SubParticipantPosition.Batter,
    )

    if (!pitcher) {
      return payload
    }

    return {
      ...payload,
      flags: [SubParticipantFlag.PossibleBatter],
    }
  })

  const benchPitchersPayload = lineup.pitcher
    .filter(
      (pitcher) =>
        !lineup.batter.some((batter) => isPlayerSame(batter, pitcher)),
    )
    .map((pitcher) => ({
      ...getUpdateLineupsPlayerPayload(pitcher, SubParticipantPosition.Pitcher),
      flags: [],
    }))

  return [
    ...battingRotationPayload,
    ...benchPitchersPayload,
    ...getUpdateLineupsPayload(lineup.coach, SubParticipantPosition.Coach),
    ...getUpdateLineupsPayload(
      lineup.willNotPlay,
      SubParticipantPosition.WillNotPlay,
    ),
  ]
}
